import { AlertTriangle } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useConfig } from "@/hooks/use-config";

interface PremiumDateBannerProps {
  selectedDate: DateTime;
}

export function PremiumDateBanner({ selectedDate }: PremiumDateBannerProps) {
  const { t } = useTranslation("common");
  const { value: historyLimitDays = 3 } = useConfig(
    "premium_features.appointment_history_limit_days"
  );

  // Calculate if selected date is beyond limit
  const isDateBeyondLimit = useMemo(() => {
    if (historyLimitDays === -1) return false; // Unlimited access

    const cutoffDate = DateTime.now().minus({ days: historyLimitDays });
    return selectedDate < cutoffDate.startOf("day");
  }, [selectedDate, historyLimitDays]);

  // Calculate days beyond limit for messaging
  const daysBeyondLimit = useMemo(() => {
    if (!isDateBeyondLimit || historyLimitDays === -1) return 0;

    const cutoffDate = DateTime.now().minus({ days: historyLimitDays });
    return Math.floor(cutoffDate.diff(selectedDate, "days").days);
  }, [selectedDate, historyLimitDays, isDateBeyondLimit]);

  // Show banner if history limitation is enabled (not unlimited)
  if (!isDateBeyondLimit) return null;

  const daysBeyondText =
    daysBeyondLimit > 0
      ? t("premium.banners.limitedHistory.daysBeyond", {
          count: daysBeyondLimit,
        })
      : "";

  const limitDaysText =
    historyLimitDays === 1
      ? t("premium.banners.limitedHistory.limitDays_singular", {
          count: historyLimitDays,
          defaultValue: "{{count}} day",
        })
      : t("premium.banners.limitedHistory.limitDays", {
          count: historyLimitDays,
          defaultValue: "{{count}} days",
        });

  return (
    <Alert className="mb-4 border-amber-200 bg-amber-50 relative">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-800 pr-8">
        {t("premium.banners.limitedHistory.title")}
      </AlertTitle>
      <AlertDescription className="text-amber-700">
        <Trans
          i18nKey="premium.banners.limitedHistory.description"
          ns="common"
          values={{
            date: selectedDate.toFormat("MMM dd, yyyy"),
            daysBeyond: daysBeyondText,
            limitDays: limitDaysText,
          }}
          components={{ strong: <strong /> }}
        />
      </AlertDescription>
    </Alert>
  );
}
